import getSymbolFromCurrency from "currency-symbol-map";
import { find, isEmpty } from "lodash";
import numbro from "numbro";
import { FormattedDate, FormattedNumber } from "react-intl";
import { PaymentPlanType } from "../../../../PaymentContainerClient/commons/enums";
import PaymentRequestDto from "../../../../PaymentContainerClient/dtos/PaymentRequestDto";
import getFormattedMessageWithScope from "../../../../utils/getFormattedMessageWithScope";
import {
    getDeadline,
    getTotalPaid,
    getTotalRemaining,
    hasAllPaymentsExpired,
    hasExpired,
} from "../../../commons/calculators";
import getFirstNotPaidNotScheduledNotOnHoldInstallment from "../../../commons/getFirstNotPaidNotScheduledNotOnHoldInstallment";
import getFirstScheduledInstallment from "../../../commons/getFirstScheduledInstallment";
import getLastNotPaidNotScheduledInstallment from "../../../commons/getLastNotPaidNotScheduledInstallment";
import { Locale, useLanguage } from "../../LanguageProvider";
import "./style.css";

const fm = getFormattedMessageWithScope("components.Summary.SummaryBox");

interface SummaryBoxProps {
    paymentRequest?: PaymentRequestDto;
    isPayNow: boolean;
    selectedPaymentPlanId?: string;
}

export const SummaryBox = ({
    paymentRequest,
    isPayNow,
    selectedPaymentPlanId,
}: SummaryBoxProps) => {
    const { locale } = useLanguage();

    const arePaymentsExpired = paymentRequest
        ? hasAllPaymentsExpired(paymentRequest)
        : false;

    const selectedPaymentPlan = find(paymentRequest?.availablePaymentPlans, {
        id: selectedPaymentPlanId,
    });
    const hasPayInInstallmentsOnDeadlineSelected =
        !isPayNow &&
        selectedPaymentPlan?.type === PaymentPlanType.MultipleInstallments;

    const nextDueDate = paymentRequest
        ? hasPayInInstallmentsOnDeadlineSelected && selectedPaymentPlan
            ? !isEmpty(paymentRequest.payments)
                ? getFirstScheduledInstallment(
                      selectedPaymentPlan.installments,
                      paymentRequest.payments
                  )
                    ? getFirstScheduledInstallment(
                          selectedPaymentPlan.installments,
                          paymentRequest.payments
                      )?.dueDate
                    : getLastNotPaidNotScheduledInstallment(
                          selectedPaymentPlan.installments,
                          paymentRequest.payments
                      )?.dueDate
                : getFirstNotPaidNotScheduledNotOnHoldInstallment(
                      selectedPaymentPlan.installments,
                      paymentRequest.payments
                  )
                ? getFirstNotPaidNotScheduledNotOnHoldInstallment(
                      selectedPaymentPlan.installments,
                      paymentRequest.payments
                  )?.dueDate
                : getDeadline(paymentRequest)
            : getDeadline(paymentRequest)
        : undefined;

    return (
        <div className="c-Summary-summary-box">
            <span className="c-Summary-summary-box-payment-reason">
                {paymentRequest && !hasExpired(paymentRequest)
                    ? paymentRequest.paymentReason
                    : "---"}
            </span>
            <div className="c-Summary-summary-box-row c-Summary-summary-box-row-deadline">
                {fm(
                    hasPayInInstallmentsOnDeadlineSelected
                        ? "nextInstallmentDeadline"
                        : "deadline"
                )}{" "}
                <span className="c-Summary-summary-box-row-deadline-value">
                    {paymentRequest && !hasExpired(paymentRequest) ? (
                        <span
                            className={
                                arePaymentsExpired
                                    ? "c-Summary-summary-box-row-deadline-value-expired"
                                    : undefined
                            }
                        >
                            {nextDueDate ? (
                                <FormattedDate value={nextDueDate} />
                            ) : (
                                "-"
                            )}
                        </span>
                    ) : (
                        "--"
                    )}
                </span>
            </div>
            <div className="c-Summary-summary-box-row">
                {fm("totalPaymentAmount")}{" "}
                <span className="c-Summary-summary-box-row-value">
                    {paymentRequest && !hasExpired(paymentRequest) ? (
                        <FormattedNumber
                            style="currency"
                            currency={paymentRequest.totalAmount.currency}
                            value={numbro(
                                paymentRequest.totalAmount.amount
                            ).value()}
                        />
                    ) : (
                        "--"
                    )}
                </span>
            </div>
            <div className="c-Summary-summary-box-row">
                {fm("alreadySettled")}{" "}
                <span className="c-Summary-summary-box-row-value">
                    {paymentRequest && !hasExpired(paymentRequest) ? (
                        <>
                            {locale !== Locale.ItIt &&
                                getSymbolFromCurrency(
                                    paymentRequest.totalAmount.currency
                                )}
                            <span className="c-Summary-summary-box-row-total-paid-value">
                                <FormattedNumber
                                    style="decimal"
                                    minimumFractionDigits={2}
                                    maximumFractionDigits={2}
                                    value={getTotalPaid(
                                        paymentRequest.payments
                                    )}
                                />
                            </span>
                            {locale === Locale.ItIt &&
                                getSymbolFromCurrency(
                                    paymentRequest.totalAmount.currency
                                )}
                        </>
                    ) : (
                        "--"
                    )}
                </span>
            </div>
            {arePaymentsExpired &&
            paymentRequest &&
            !hasExpired(paymentRequest) ? (
                <div className="c-Summary-summary-box-row">
                    {fm("expired")}{" "}
                    <span className="c-Summary-summary-box-row-value">
                        {locale !== Locale.ItIt &&
                            getSymbolFromCurrency(
                                paymentRequest.totalAmount.currency
                            )}

                        <span className="c-Summary-summary-box-row-value-expired">
                            <FormattedNumber
                                style="decimal"
                                minimumFractionDigits={2}
                                maximumFractionDigits={2}
                                currency={paymentRequest.totalAmount.currency}
                                value={getTotalRemaining(
                                    paymentRequest.totalAmount.amount,
                                    paymentRequest.payments
                                )}
                            />
                        </span>
                        {locale === Locale.ItIt && (
                            <span className="c-Summary-summary-box-row-total-expired-value-currency">
                                {getSymbolFromCurrency(
                                    paymentRequest.totalAmount.currency
                                )}
                            </span>
                        )}
                    </span>
                </div>
            ) : undefined}
            <div className="c-Summary-summary-box-row-total-remaining">
                <span className="c-Summary-summary-box-row-total-remaining-label">
                    {fm("totalRemaining")}
                </span>{" "}
                <span className="c-Summary-summary-box-row-value">
                    {paymentRequest && !hasExpired(paymentRequest) ? (
                        <span className="c-Summary-summary-box-row-total-remaining-value">
                            <FormattedNumber
                                style="currency"
                                currency={paymentRequest.totalAmount.currency}
                                value={getTotalRemaining(
                                    paymentRequest.totalAmount.amount,
                                    paymentRequest.payments
                                )}
                            />
                        </span>
                    ) : (
                        <div className="c-Summary-summary-box-row-total-remaining-empty-value" />
                    )}
                </span>
            </div>
        </div>
    );
};
